/**
 * Shared centered section label: hairline rules around a soft tinted capsule.
 */

import { IonIcon } from "@ionic/react";
import { chevronDownOutline } from "ionicons/icons";
import React, { useMemo, type CSSProperties, type ReactNode } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { triggerSelectionHaptic } from "../../tools/haptics.tools";

/** Default capsule width when {@link SectionLabelProps.isFixedWidth} is enabled. */
const DEFAULT_FIXED_CAPSULE_WIDTH_PX = 200;

interface SectionLabelProps {

    /** Visible section title inside the capsule. */
    "label": string;

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi": boolean;

    /** Optional leading icon rendered in the capsule. */
    "icon"?: ReactNode;

    /** Stable DOM id for the section wrapper. */
    "id"?: string;

    /** Optional class names on the section wrapper. */
    "className"?: string;

    /** Heading level for the capsule title when not interactive. Defaults to `h2`. */
    "headingLevel"?: "h2" | "h3" | "p";

    /**
     * When set with {@link onToggleExpand}, the capsule becomes an accordion control
     * (chevron + `aria-expanded`) instead of a static title.
     */
    "isExpanded"?: boolean;

    /** Toggles accordion expansion; omit for a static section label. */
    "onToggleExpand"?: () => void;

    /**
     * Optional item count shown in a badge when the accordion is collapsed
     * (and always available for screen readers via the control label).
     */
    "itemCount"?: number;

    /**
     * When `true`, locks the capsule to {@link width} so sibling section titles stay aligned.
     * Defaults to `false` (content-sized capsule).
     */
    "isFixedWidth"?: boolean;

    /**
     * Capsule width in pixels when {@link isFixedWidth} is `true`.
     * Ignored otherwise. Defaults to `200`.
     */
    "width"?: number;
}

/**
 * Centered section label with hairline rules around a soft capsule.
 *
 * @remarks Used across mini-modals, car selection, and saved-itinerary temporal groups.
 * Sections that pass {@link onToggleExpand} turn the capsule into an accordion header.
 * Icon and capsule tint use theme-aware greens so contrast stays readable in dark UI.
 *
 * @param props Section label props.
 * @returns Labeled divider row, optionally interactive as an accordion header.
 */
const SectionLabel: React.FC<SectionLabelProps> = ( {
    label,
    isDarkUi,
    icon,
    id,
    className = "",
    headingLevel = "h2",
    isExpanded = true,
    onToggleExpand,
    itemCount,
    isFixedWidth = false,
    width = DEFAULT_FIXED_CAPSULE_WIDTH_PX,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const HeadingTag = headingLevel;
    const isAccordion = onToggleExpand !== undefined;
    const chevronTransition = reducedMotion ? "none" : "transform 180ms cubic-bezier(0.2, 0.8, 0.2, 1)";
    const showCollapsedCount = isAccordion && !isExpanded && typeof itemCount === "number" && itemCount > 0;

    /**
     * Theme-aware green: darker `#0C9570` in light, lighter mint in dark for contrast
     * on navy overlays ({@link UIColorPalette.statusOnTimeText}).
     */
    const iconAccent = palette.statusOnTimeText;

    const capsuleStyle = useMemo( (): CSSProperties => ( {
        "borderColor": isDarkUi ? palette.overlayBorderStrong : palette.overlayBorder,
        "background":  isDarkUi
            ? `linear-gradient(118deg, ${ iconAccent }28 0%, ${ palette.overlayCardBackgroundStrong } 52%, ${ palette.overlayCardBackgroundSoft } 100%)`
            : `linear-gradient(118deg, ${ iconAccent }1A 0%, ${ palette.overlayCardBackgroundStrong } 55%, ${ palette.overlayCardBackgroundSoft } 100%)`,
        "color":     palette.overlayTextSecondary,
        "boxShadow": isDarkUi ? "0 1px 6px rgba(0, 0, 0, 0.22)" : "0 1px 4px rgba(15, 23, 42, 0.04)",
        ...isFixedWidth
            ? {
                "width":          `${ width }px`,
                "justifyContent": "center",
            }
            : {},
    } ), [
        iconAccent,
        isDarkUi,
        isFixedWidth,
        palette.overlayBorder,
        palette.overlayBorderStrong,
        palette.overlayCardBackgroundSoft,
        palette.overlayCardBackgroundStrong,
        palette.overlayTextSecondary,
        width,
    ] );

    const countBadgeStyle = useMemo( (): CSSProperties => ( {
        "backgroundColor": isDarkUi ? `${ iconAccent }33` : `${ iconAccent }22`,
        "color":           iconAccent,
    } ), [ iconAccent, isDarkUi ] );

    /**
     * Handles accordion tap with selection haptic feedback.
     */
    const handleToggle = (): void => {
        onToggleExpand?.();
        void triggerSelectionHaptic();
    };

    const capsuleContent = (
        <>
            {icon != null
                ? (
                    <span
                        aria-hidden={true}
                        className="inline-flex w-3.5 shrink-0 items-center justify-center"
                        style={{ "color": iconAccent }}
                    >
                        {icon}
                    </span>
                )
                : null}
            <span
                className={isFixedWidth ? "min-w-0 flex-1 truncate text-center" : undefined}
                style={{ "color": palette.overlayTextPrimary }}
            >
                {label}
            </span>
            {isAccordion
                ? (
                    <span
                        aria-hidden={true}
                        className={`inline-flex h-[16px] min-w-[16px] shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-bold tabular-nums leading-none ${ showCollapsedCount ? "" : "invisible" }`}
                        style={countBadgeStyle}
                    >
                        {itemCount ?? 0}
                    </span>
                )
                : null}
            {isAccordion
                ? (
                    <IonIcon
                        aria-hidden={true}
                        className="shrink-0 opacity-70"
                        icon={chevronDownOutline}
                        style={{
                            "fontSize":   "14px",
                            "color":      iconAccent,
                            "transform":  isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            "transition": chevronTransition,
                        }}
                    />
                )
                : null}
        </>
    );

    return (
        <div
            className={`flex items-center gap-2 px-1 ${ className }`}
            id={id}
        >
            <span
                aria-hidden={true}
                className="h-px min-w-0 flex-1 rounded-full"
                style={{ "backgroundColor": palette.overlayBorder }}
            />
            {isAccordion
                ? (
                    <button
                        aria-expanded={isExpanded}
                        aria-label={isExpanded
                            ? `Replier la section ${ label }`
                            : `Déplier la section ${ label }${ typeof itemCount === "number" ? `, ${ itemCount } itinéraires` : "" }`}
                        className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-solid px-2.5 py-1 text-[11px] font-semibold tracking-wide backdrop-blur-sm transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        onClick={handleToggle}
                        style={capsuleStyle}
                        type="button"
                    >
                        {capsuleContent}
                    </button>
                )
                : (
                    <HeadingTag
                        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-solid px-2.5 py-1 text-[11px] font-semibold tracking-wide backdrop-blur-sm"
                        style={capsuleStyle}
                    >
                        {capsuleContent}
                    </HeadingTag>
                )}
            <span
                aria-hidden={true}
                className="h-px min-w-0 flex-1 rounded-full"
                style={{ "backgroundColor": palette.overlayBorder }}
            />
        </div>
    );
};

export default SectionLabel;
